import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function FoiParcursComodatPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/foi-parcurs-comodat")} />
      <ArticleLayout
        eyebrow="Folosință vehicul"
        title="Foi de parcurs și comodat: mașina personală folosită de PFA sau SRL"
        lead="Dacă autoturismul e pe numele persoanei fizice, iar cheltuielile trec pe PFA sau pe firmă, primul document nu e foaia de parcurs. Este dreptul de folosință — de regulă un contract de comodat. Fără el, bonurile pe CUI nu țin la control."
      >
        <p>
          Comodatul răspunde la o întrebare simplă: <strong>are activitatea dreptul să
          folosească mașina asta?</strong> Foaia de parcurs răspunde la alta:{" "}
          <strong>a fost folosită exclusiv în scop economic?</strong> Le amesteci, și la
          control cade tot dosarul — nu doar „un detaliu de contract".
        </p>
        <p>
          Situația e frecventă. Titularul PFA sau administratorul SRL conduce mașina de
          acasă, alimentează pe CUI, iar talonul e pe persoană fizică. Foaia poate fi
          corectă ca formă. Inspectorul întreabă totuși de ce combustibilul, RCA-ul sau
          service-ul apar pe entitate, pentru un bun care nu e al ei.
        </p>

        <h2>Când ai nevoie de comodat</h2>
        <p>
          Când vehiculul <strong>nu e în patrimoniul</strong> PFA-ului sau al SRL-ului (nu
          e mijloc fix, nu e factură pe CUI, nu e leasing pe entitate) și totuși vrei să
          treci pe activitate cheltuieli legate de el: combustibil, întreținere, asigurări,
          piese.
        </p>
        <p>
          Pentru PFA, cele trei situații tipice rămân: mașină personală (comodat persoană
          fizică → PFA), mașină cumpărată pe PFA, leasing pe PFA. Doar prima cere comodat.
          Pentru SRL, același principiu: mașina administratorului sau a unui asociat,
          folosită ca mașină de serviciu, cere drept de folosință documentat.
        </p>
        <p>
          Dacă mașina e deja pe CUI sau în leasing pe entitate, comodatul nu e piesa
          centrală. Atunci discuția e 50% versus 100% și foaia de parcurs — vezi{" "}
          <Link to="/ghid-foaie-parcurs-anaf">ghidul ANAF</Link> și{" "}
          <Link to="/deducere-100-combustibil">articolul despre deducerea 100%</Link>.
        </p>

        <h2>Comodat sau decont de kilometri — nu pe aceleași curse</h2>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Situație</th>
                <th>Ce documentezi</th>
                <th>Combustibil pe CUI?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Mașina e, de fapt, mașina de lucru a activității</td>
                <td>Comodat + (pentru 100%) foaie de parcurs</td>
                <td>Da, cu limitele 50% / 100%</td>
              </tr>
              <tr>
                <td>Deplasări ocazionale (client, instituție, furnizor)</td>
                <td>Decont lei/km, cu traseu și dovada plății</td>
                <td>De regulă nu</td>
              </tr>
              <tr>
                <td>Mix personal + activitate, fără reguli</td>
                <td>Nici comodatul, nici decontul nu „spală" situația</td>
                <td>Risc de recalculare</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          <strong>Comodat:</strong> proprietarul pune mașina <strong>gratuit</strong> la
          dispoziția PFA-ului sau SRL-ului. Entitatea poate suporta apoi cheltuielile, cu
          aceleași reguli ca la un vehicul din patrimoniu. <strong>Decont:</strong>{" "}
          entitatea <strong>nu preia</strong> mașina. Persoana fizică se deplasează, iar
          PFA-ul/SRL-ul plătește kilometrii de serviciu. Nu treci și alimentările pe CUI
          pentru aceleași rute: inspectorul vede dublarea. Actele decontului sunt în{" "}
          <Link to="/decont-kilometri">articolul despre decontul de kilometri</Link>.
        </p>

        <h2>Ce trebuie să conțină contractul</h2>
        <p>
          Nu există formular ANAF tipizat. Contează că dreptul de folosință e{" "}
          <strong>scris, semnat și datat înainte</strong> de perioada pe care o justifici.
          Elementele minime:
        </p>

        <h3>Părțile contractului</h3>
        <p>
          Persoana fizică (proprietar) și PFA-ul sau SRL-ul (utilizator), cu CUI-ul
          fiecăruia clar identificat.
        </p>

        <h3>Identificarea vehiculului</h3>
        <p>
          Marcă, model, număr de înmatriculare, serie de șasiu. Contractul trebuie să
          identifice clar mașina.
        </p>

        <h3>Scopul comodatului</h3>
        <p>
          Folosință în activitatea economică, nu un text generic. Scopul justifică de
          ce cheltuielile pot trece pe firmă.
        </p>

        <h3>Durata contractului</h3>
        <p>
          Dată de început (și de sfârșit, dacă e cazul). Comodatul trebuie semnat
          înainte de perioada pentru care deduci cheltuieli.
        </p>

        <h3>Cine suportă cheltuielile</h3>
        <p>
          Combustibil, RCA, CASCO, ITP, service, anvelope — clar stipulat în contract.
        </p>

        <h3>Cine poate conduce</h3>
        <p>
          Titular, administrator sau angajați nominalizați. Important pentru dosar și
          asigurări.
        </p>

        <p>
          Păstrează o copie cu <strong>dată certă</strong> (înregistrare la ANAF sau formă
          care face opozabilă data). Un comodat „semnat ieri" pentru cheltuieli de doi ani
          nu ajută. La control, lipsa contractului e greșeala specifică PFA-urilor cu
          mașină personală: <strong>fără comodat scris nu deduci nimic, indiferent de
          foaie</strong>.
        </p>

        <h2>Ce rol are foaia de parcurs după comodat</h2>
        <p>
          Comodatul deschide ușa. Nu închide discuția despre 50% și 100%. Pentru vehicule
          sub 3,5 tone și sub 9 scaune, regula implicită rămâne <strong>50%</strong> fără
          foaie. <strong>100%</strong> cere utilizare exclusiv economică, dovedită pe foaia
          de parcurs cu cele 4 elemente (pct. 16 alin. (2) din normele art. 25 și, pentru
          TVA, pct. 68 alin. (2) din normele art. 298): categorie, scop și loc, kilometri,
          norma proprie de consum.
        </p>
        <p>
          Formatul e același ca la o mașină din patrimoniu: lunară e suficientă,
          electronică e acceptată, scopul trebuie să fie concret. Detaliile de model sunt
          în <Link to="/foi-parcurs-pfa">ghidul PFA</Link> și în{" "}
          <Link to="/model-foaie-parcurs">articolul despre modelul de foaie</Link>. Aici
          contează ordinea: <strong>întâi dreptul de folosință, apoi evidența
          deplasărilor</strong>.
        </p>
        <p>
          Pentru 100% la SRL, regulamentul intern care interzice uzul personal rămâne în
          dosar lângă comodat. La PFA, disciplina e aceeași: trasee de lucru, nu weekend
          și vacanță trecute ca „deplasare serviciu".
        </p>

        <h2>Greșeli care cad la control</h2>

        <h3>Cheltuieli pe CUI fără comodat</h3>
        <p>
          Fără contract de comodat (sau alt drept de folosință clar), cheltuielile pe
          firmă pentru o mașină a persoanei fizice nu au justificare.
        </p>

        <h3>Comodat fără dată certă</h3>
        <p>
          Contract semnat după perioada controlată sau fără înregistrare care să
          dovedească data. La control, ordinea contează.
        </p>

        <h3>Facturi pe persoana fizică, deduse la firmă</h3>
        <p>
          Bonurile și facturile trebuie să fie pe CUI-ul entității, nu pe numele
          proprietarului.
        </p>

        <h3>Decont și alimentări pe aceleași curse</h3>
        <p>
          Nu poți deconta km și totodată trece combustibilul pe firmă pentru aceleași
          trasee. Inspectorul vede dublarea.
        </p>

        <h3>Foaie perfectă, contract lipsă</h3>
        <p>
          Foaia de parcurs nu compensează lipsa comodatului. Dreptul de folosință e
          primul document.
        </p>

        <h3>Alimentări în concediu sau weekend</h3>
        <p>
          Combustibil fără traseu economic ridică suspiciuni. Dacă mașina e folosită
          mixt, regimul de 50% e mai sigur.
        </p>

        <h2>Dosarul minim, pe vehicul</h2>
        <p>
          Ține la un loc: contractul de comodat, talon, ITP, RCA, foile lunare, bonurile
          de combustibil, eventuale facturi de service. În{" "}
          <Link to="/control-anaf-cheltuieli-autoturism">ghidul de control ANAF</Link>,
          contractul de comodat e în checklist lângă leasing și regulament. Inspectorul
          trebuie să poată citi, într-un singur dosar, <strong>cine are dreptul</strong> și{" "}
          <strong>ce s-a întâmplat pe traseu</strong>.
        </p>

        <h2>Concluzie</h2>
        <p>
          Dacă mașina e a persoanei fizice și o folosești în activitate,{" "}
          <strong>comodatul e primul pas</strong>, nu foaia. Apoi alegi 50% (fără foaie,
          fără pretenția de exclusivitate) sau 100% (foaie corectă, trasee reale, consum
          care se leagă de alimentări).
        </p>
        <p>
          Ghidul PFA rămâne pagina despre obligațiile PFA-ului. Articolul de față e pagina
          despre <strong>comodat</strong>. Pentru calculul 50% versus 100%, vezi{" "}
          <Link to="/deducere-100-combustibil">deducerea de combustibil</Link>. Pentru
          lista de documente la inspecție, vezi{" "}
          <Link to="/control-anaf-cheltuieli-autoturism">controlul ANAF pe cheltuieli auto</Link>.
        </p>
        <RelatedGuides currentPath="/foi-parcurs-comodat" />
      </ArticleLayout>
    </>
  );
}
